import React, { ReactNode } from "react";
import { SearchIcon } from "@heroicons/react/solid";
import clsx from "clsx";

interface DebouncedInputProps {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
  height?: "large" | "middle" | "small";
  className?: string;
  labelName?: string;
  containerClassName?: string;
  iconClassName?: string;
  icon?: ReactNode;
  leadingIcon?: ReactNode;
  showIcon?: boolean;
  hasError?: boolean;
  inputWrapperClassName?: string;
}

const DebouncedInput = React.forwardRef(
  (
    {
      value: initialValue,
      onChange,
      debounce = 500,
      height = "large",
      className,
      labelName,
      containerClassName,
      leadingIcon,
      icon,
      iconClassName,
      showIcon = true,
      ...props
    }: DebouncedInputProps &
      Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">,
    ref: React.Ref<HTMLInputElement>
  ) => {
    const [value, setValue] = React.useState(initialValue);

    React.useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    React.useEffect(() => {
      const timeout = setTimeout(() => {
        onChange?.(value);
      }, debounce);

      return () => clearTimeout(timeout);
    }, [value]);

    return (
      <div
        className={clsx(
          `pb-15 ${containerClassName}`,
          props.hidden ? "hidden" : "",
          { "opacity-40": props.disabled }
        )}
      >
        {labelName ? (
          <label className="text-sm flex items-center text-gray-700 mb-0.5">
            {labelName}
          </label>
        ) : null}

        <div
          className={clsx(
            "relative text-gray-400 focus-within:text-gray-600 overflow-hidden border border-gray-300 rounded-md",
            "flex items-center",
            {
              "h-large": height === "large",
              "h-middle": height === "middle",
              "h-small": height === "small",
              "pl-[40px]": leadingIcon,
              "border-red-600": props.hasError,
            },
            props.inputWrapperClassName
          )}
        >
          {leadingIcon && (
            <span className="absolute inset-y-0 left-0 h-full flex items-center w-10 border-r border-gray-300">
              {leadingIcon}
            </span>
          )}
          <input
            ref={ref}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={props.placeholder ?? ""}
            className={clsx(
              "block w-full bg-white border-none font-light p-2 leading-5 text-gray-900 text-sm",
              "focus:border-gray-300 sm:text-sm w-36",
              props.hasError ? "placeholder-red-500" : "placeholder-gray-500",
              className
            )}
            type={props.type ?? "text"}
            hidden={props.hidden}
            disabled={props.disabled}
            list={props.list}
          />
          {showIcon && (
            <div
              className={clsx(
                "pointer-events-none absolute inset-y-0 right-3 flex items-center bg-white",
                "h-fit top-0 bottom-0 my-auto",
                iconClassName
              )}
            >
              {icon || (
                <SearchIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

DebouncedInput.displayName = "DebouncedInput";

export default DebouncedInput;
